import generateStore from './generateStore.js'
import { jwtDecode } from 'jwt-decode'
import { axios } from '../request'
import { parseRetryAfter } from '../lib/sessionEnd'

// The view mode stored for this tab, or null when it was stored for anybody
// but `userId` — another user, nobody (logged out) or no one recorded.
function readViewMode(userId) {
  try {
    const stored = JSON.parse(sessionStorage.getItem('viewMode'))
    if (userId && stored?.userId === userId) {
      return stored.mode || null
    }
  } catch {
    // The pre-#625 value was the bare string 'member', which is not JSON.
  }
  return null
}

export default (app) => {
  const store = generateStore('auth', app)

  store.state.isRefreshing = false
  store.state.accessTokenExpiresAt = 0
  // #355: seconds to wait after a 429, read from the Retry-After header the API
  // has always sent but the browser could not read until it was exposed in CORS.
  // null when unknown, and the message then states no time rather than a wrong one.
  store.state.retryAfterSeconds = null

  const auth = localStorage.getItem('authentication')
  if (auth !== null) {
    store.state.jwt = jwtDecode(auth)
    store.state.accessTokenExpiresAt = localStorage.getItem('accessTokenExpiresAt')
  } else {
    store.state.jwt = {}
  }

  // #625 — «ver como cooperador» is kept in sessionStorage, which belongs to the
  // tab, and the session in localStorage, which every logout path clears
  // (Logout.vue, LoginC's URL login, request.js endSession) and nothing else
  // touches. So the stored mode names the user who turned it on, and is honoured
  // only while the token is still that user's: whoever swapped the session, in
  // this tab or in another one sharing the localStorage, the next page load
  // drops it. A value without an owner (stored before this change) is dropped
  // too — the cost is one more click on the toggle.
  store.state.viewMode = readViewMode(store.state.jwt?.CustomUserInfo?.ID)
  if (store.state.viewMode === null) {
    sessionStorage.removeItem('viewMode')
  }

  const refreshToken = localStorage.getItem('refreshToken')
  if (refreshToken !== null) {
    store.state.refreshToken = jwtDecode(refreshToken)
    store.state.refreshTokenString = refreshToken

    const refreshTokenExpiresAt = localStorage.getItem('refreshTokenExpiresAt')
    if (Math.floor(Date.now() / 1000) + 86400 > parseInt(refreshTokenExpiresAt, 10)) {
      //86400  is 24 hours
      localStorage.clear()
      if (location.pathname !== '/') {
        location.href = '/'
      }
    }
  } else {
    store.state.refreshToken = {}
  }

  store.mutations.login = function (state, payload) {
    localStorage.setItem('authentication', payload.access)
    localStorage.setItem('refreshToken', payload.refresh)
    state.authentication = payload.access
    state.jwt = jwtDecode(payload.access)

    //save accessToken
    state.jwt = jwtDecode(payload.access)
    const validityDuration = state.jwt.exp - state.jwt.iat
    const validUntil = Math.floor(Date.now() / 1000) + validityDuration
    state.accessTokenExpiresAt = validUntil
    localStorage.setItem('accessTokenExpiresAt', validUntil)

    //save refreshToken
    state.refreshTokenString = payload.refresh
    state.refreshToken = jwtDecode(payload.refresh)

    const refreshValidityDuration = state.refreshToken.exp - state.refreshToken.iat
    const refreshValidUntil = Math.floor(Date.now() / 1000) + refreshValidityDuration
    localStorage.setItem('refreshTokenExpiresAt', refreshValidUntil)

    state.errors = []
  }

  store.mutations.setRefreshing = function (state, v) {
    state.isRefreshing = v
  }

  store.mutations.setRetryAfter = function (state, seconds) {
    state.retryAfterSeconds = seconds
  }

  store.mutations.setViewMode = function (state, mode) {
    state.viewMode = mode
    if (mode) {
      sessionStorage.setItem(
        'viewMode',
        JSON.stringify({ mode, userId: state.jwt?.CustomUserInfo?.ID })
      )
    } else {
      sessionStorage.removeItem('viewMode')
    }
  }

  store.actions.create = function (context) {
    context.commit('setErrors', { listName: 'default', key: 0, payload: {} })
    context.commit('setRetryAfter', null)
    return new Promise((resolve, reject) => {
      const url = '/auth'
      axios
        .post(url, context.state.list.default.data[0])
        .then((res) => {
          context.commit('setLastStatus', res.status)
          if (res.status !== 204) {
            context.commit('login', res.data)
            // #625 — a sign-in starts in the normal view, even when it is the
            // same user coming back in this tab. Here and not in `login`, which
            // every token renewal also commits (setLoginData).
            context.commit('setViewMode', null)
          }
          resolve()
        })
        .catch((err) => {
          if (err.response) {
            context.commit('setLastStatus', err.response.status)
            if (typeof err.response.data !== 'undefined') {
              if (typeof err.response.data.errors !== 'undefined') {
                context.commit('setErrors', {
                  listName: 'default',
                  key: 0,
                  payload: err.response.data.errors,
                })
              }
            }
            if (err.response.status === 403) {
              context.commit('setErrors', {
                listName: 'default',
                key: 0,
                payload: { global: 'wrongCredentials' },
              })
            }
            if (err.response.status === 404) {
              context.commit('setErrors', {
                listName: 'default',
                key: 0,
                payload: { global: 'accountNotFound' },
              })
            }
            // #327: the auth surface is now rate limited. A 429 carries no
            // `errors` body, so without classifying it here the caller would
            // reject with no global error and the login button would show
            // nothing. The reject itself is the unconditional one below (#330).
            if (err.response.status === 429) {
              // #355: keep the advertised wait so the message can say how long
              // instead of a vague "wait a moment". LoginC decides the wording —
              // on the day of a token-invalidating deploy the budget was spent by
              // the app's own renewals, not by anybody mistyping a password.
              context.commit(
                'setRetryAfter',
                parseRetryAfter(err.response.headers?.['retry-after'])
              )
              context.commit('setErrors', {
                listName: 'default',
                key: 0,
                payload: { global: 'tooManyAttempts' },
              })
            }
          } else {
            // #379: no `response` at all — the request never reached the API
            // (connection down, DNS, timeout). Every branch above needs one, so
            // this case left `global` unset while LoginC still drew its warning
            // box: an empty yellow rectangle for three seconds, and no hint that
            // the problem was the connection rather than the password.
            context.commit('setErrors', {
              listName: 'default',
              key: 0,
              payload: { global: 'networkError' },
            })
          }
          // Always reject, on every error path — including a response-less
          // network/timeout failure (err.response undefined) and the 404 branch.
          // Callers (LoginC, ForgotC, Recover) chain a .catch to surface the
          // failure; without this the promise stayed pending forever and every
          // error path was invisible.
          reject()
        })
    })
  }
  store.actions.recoverUserPassword = function (context, email) {
    return new Promise((resolve, reject) => {
      const body = {
        email: email,
        recover_password: true,
      }
      const url = '/auth'
      axios
        .post(url, body)
        .then((res) => {
          resolve(res)
        })
        .catch((err) => {
          reject(err)
        })
    })
  }
  store.actions.setRefreshing = function (context, payload) {
    context.commit('setRefreshing', payload)
  }

  store.actions.toggleViewMode = function (context) {
    const current = context.state.viewMode
    context.commit('setViewMode', current === 'member' ? null : 'member')
  }

  store.actions.setLoginData = function (context, payload) {
    context.commit('login', payload)
    context.dispatch('settings/checkIfRefreshIsNeeded', null, { root: true })
    // #379: same floating dispatch as in request.js `applyRefreshedSession`, and
    // the same reason for the catch — this runs right after a renewal, and the
    // explainer is UI enrichment that must never take the app down with it.
    context.dispatch('explainer/load', null, { root: true }).catch((err) => console.error(err))
  }

  if (typeof store.getters === 'undefined') {
    store.getters = {}
  }

  // #383 — FROZEN FOR THE LIFE OF THE PAGE, on purpose but worth knowing.
  //
  // It reads localStorage, which Vuex has no way to track, so Vue computes it
  // once and caches it: no consumer is ever told it changed. That is safe only
  // because every transition of the value is a full page navigation —
  // LoginC.redirectToDashboard() on the way in, Logout.vue and request.js
  // endSession() on the way out, all three setting window.location.
  //
  // So do not add a watcher on it and do not gate anything on it changing.
  // App.vue's `watch: isLoggedin` is already dead code for this reason: it can
  // never fire, and the pronobis hydrate it exists for happens in mounted() on
  // the same page load anyway. Making a logout an in-SPA transition means
  // fixing this getter AND clearing state.jwt (nothing does today, so
  // isAdminOrEditor would stay true), and re-checking the nine reads across
  // App, NavigationC, Notifications, Footer, Verify, LoginC and
  // actionableCounts.
  store.getters.isLoggedin = function () {
    const auth = localStorage.getItem('authentication')
    if (auth !== null) {
      return true
    }
    return false
  }
  store.getters.role = function (state) {
    if (typeof state.jwt === 'undefined') {
      return undefined
    }
    if (typeof state.jwt.CustomUserInfo === 'undefined') {
      return undefined
    }

    return state.jwt.CustomUserInfo.Role
  }

  store.getters.effectiveRole = function (state, getters) {
    if (state.viewMode === 'member' && getters.canToggleViewMode) {
      return 'member'
    }
    return getters.role
  }

  store.getters.isAdminOrEditor = function (state, getters) {
    return ['admin', 'editor'].includes(getters.effectiveRole)
  }

  store.getters.isAdmin = function (state, getters) {
    return getters.effectiveRole === 'admin'
  }

  // Current user's id (from the JWT). Used e.g. by the #198 gestor filter to
  // exclude the current admin from the "other gestores" dropdown options.
  store.getters.userId = function (state) {
    return state.jwt?.CustomUserInfo?.ID
  }

  // Current user's display name (from the JWT) — the same value shown in the
  // nav. The #198 gestor filter uses it to label the default "my queue" option.
  store.getters.userName = function (state) {
    return state.jwt?.CustomUserInfo?.Name
  }

  store.getters.canToggleViewMode = function (state, getters) {
    const realRole = getters.role
    if (!['admin', 'editor'].includes(realRole)) {
      return false
    }
    if (typeof state.jwt === 'undefined' || typeof state.jwt.CustomUserInfo === 'undefined') {
      return false
    }
    return !!state.jwt.CustomUserInfo.MemberID
  }

  store.getters.isMemberView = function (state) {
    return state.viewMode === 'member'
  }

  return store
}
